import Big from 'big.js';
import { useMemo } from 'react';
import { useForm } from 'react-final-form';
import { parseBig } from 'utilities';
import type { Expense } from 'modules/invoicing/expenses/types';
import type { ImportedBlocksLinkerProps, ImportedBlockGroup } from './types';
import type { LinkerContext } from '../types';

function calculateState(
  expense: Expense,
  linkerContext: LinkerContext,
  boundValues: Record<string, string> | undefined,
) {
  const allGroups: Record<string, ImportedBlockGroup> = {};
  (expense.rows || []).forEach((row) => {
    if (!row.block_code) {
      return;
    }
    if (!(row.block_code in allGroups)) {
      allGroups[row.block_code] = {
        blockCode: row.block_code,
        block: Object.values(linkerContext.entities.block).find((b) => b.code === row.block_code),
        expenseAmount: parseBig(row.total),
        expenseRows: [row],
        linkableAmount: Big(0),
        linkableRows: [],
        canAutoLink: false,
        hasLinks: false,
        isVisible: false,
      };
    } else {
      allGroups[row.block_code].expenseAmount = allGroups[row.block_code].expenseAmount.plus(row.total);
      allGroups[row.block_code].expenseRows.push(row);
    }
  });

  Object.values(allGroups).forEach((group) => {
    const matchingLinkable = linkerContext.linkable.filter((l) => {
      const block = linkerContext.entities.block[Number(l.block_id)];
      return block && block.code === group.blockCode;
    });

    matchingLinkable.forEach((linkable) => {
      const linkableId = Number(linkable.id);
      const isAlreadyLinked = boundValues?.[`_${linkableId}`] !== undefined;
      if (isAlreadyLinked) {
        group.hasLinks = true;
      } else {
        const balance = parseBig(linkable.balance);
        if (balance.gt(0)) {
          group.linkableRows.push({
            id: linkableId,
            amount: balance,
          });
          group.linkableAmount = group.linkableAmount.plus(balance);
        }
      }
    });

    if (group.linkableAmount.eq(0)) {
      group.canAutoLink = false;
      group.reason = 'No linkable rows found';
    } else if (!group.expenseAmount.eq(group.linkableAmount)) {
      group.canAutoLink = false;
      group.reason = `Amount mismatch. Can link only if expense amount equals balance.`;
    } else if (group.linkableRows.length === 0) {
      group.canAutoLink = false;
      group.reason = 'No available linkable rows';
    } else {
      group.canAutoLink = true;
    }

    group.isVisible = !group.hasLinks;
  });

  const groups = Object.values(allGroups).filter((g) => g.isVisible);
  const validGroups = groups.filter((g) => g.canAutoLink);
  const invalidGroups = groups.filter((g) => !g.canAutoLink);

  const totals = {
    valid: {
      expenseAmount: validGroups.reduce((sum, g) => sum.plus(g.expenseAmount), Big(0)),
      linkableAmount: validGroups.reduce((sum, g) => sum.plus(g.linkableAmount), Big(0)),
    },
    invalid: {
      expenseAmount: invalidGroups.reduce((sum, g) => sum.plus(g.expenseAmount), Big(0)),
      linkableAmount: invalidGroups.reduce((sum, g) => sum.plus(g.linkableAmount), Big(0)),
    },
  };

  return { groups, totals };
}

export function useImportedBlocksLinker({ expense, linkerContext }: ImportedBlocksLinkerProps) {
  const form = useForm();
  const formValues = form.getState().values;

  // Calculate the linker state
  const { groups, totals } = useMemo(
    () => calculateState(expense, linkerContext, formValues.bound),
    [expense, linkerContext, formValues.bound],
  );

  const handleLinkAll = () => {
    groups.forEach((group) => {
      if (group.canAutoLink) {
        let remaining = group.expenseAmount;
        group.linkableRows.forEach((linkableRow) => {
          if (remaining.lte(0)) {
            return;
          }
          const amountToLink = remaining.lte(linkableRow.amount) ? remaining : linkableRow.amount;
          form.change(`bound._${linkableRow.id}`, amountToLink.toFixed());
          remaining = remaining.minus(amountToLink);
        });
      }
    });
  };

  return {
    groups,
    totals,
    handleLinkAll,
  };
}
